/**
 * @file     CardCtaInquiryRoot.tsx
 * @readme   ../CLAUDE.md
 * @purpose  Inquiry-only CardCta island. Loaded only on surfaces whose
 *           CARD_CTA kind is inquiry, so alert/link SEO hubs do not pull
 *           LeadCaptureModal into their client bundle.
 */
'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { LeadCaptureModal } from '@/components/lead-capture/LeadCaptureModal';
import { LeadCaptureProvider, useLeadCapture } from '@/components/lead-capture/LeadCaptureContext';
import { CardCtaContext, type CardCtaHandle, type CardCtaListing } from './CardCtaContext';
import type { CardCtaSurface } from '../cardCta';

function InquiryCardCta({
  surface,
  slug,
  label,
  children,
}: {
  surface: CardCtaSurface;
  slug: string;
  label: string;
  children: ReactNode;
}) {
  const { trackCtaClick } = useAnalytics();
  const { open } = useLeadCapture();

  const onCardCta = useCallback((listing?: CardCtaListing) => {
    trackCtaClick(label, `card_cta_${surface}`, slug);
    if (!listing?.listingKey) return;
    // listingSlug on the capture payload is the listing_key the inquiries
    // API stores. Same contract as the liked-list Inquire button.
    open({
      mode: 'inquiry',
      listing: {
        listingSlug: listing.listingKey,
        listingSource: listing.source,
        listingTitle: listing.title,
        listingPrice: listing.price,
        listingMlsNumber: listing.mlsId,
      },
    });
  }, [label, open, slug, surface, trackCtaClick]);

  const handle = useMemo<CardCtaHandle>(() => ({
    label,
    href: null,
    onActivate: onCardCta,
    done: false,
    kind: 'inquiry',
  }), [label, onCardCta]);

  return <CardCtaContext.Provider value={handle}>{children}</CardCtaContext.Provider>;
}

export default function CardCtaInquiryRoot({
  surface,
  slug,
  label,
  children,
}: {
  surface: CardCtaSurface;
  slug: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <LeadCaptureProvider>
      <InquiryCardCta surface={surface} slug={slug} label={label}>
        {children}
      </InquiryCardCta>
      <LeadCaptureModal />
    </LeadCaptureProvider>
  );
}
